import type { Deck, DeckPlaystyle } from '../types/deck';
import { legendIdentityFromRef } from './championIdentity';
import { deckMatchesGalleryTextSearch } from './deckGallerySearch';

export type DeckGallerySort = 'popular' | 'newest' | 'updated';
/** Community gallery: new published lists, not recent edits. */
export const DEFAULT_DECK_GALLERY_SORT: DeckGallerySort = 'newest';
/** My Decks: the list you last touched should be first. */
export const DEFAULT_MY_DECKS_SORT: DeckGallerySort = 'updated';

export function deckGallerySortForScope(isMine: boolean): DeckGallerySort {
  return isMine ? DEFAULT_MY_DECKS_SORT : DEFAULT_DECK_GALLERY_SORT;
}

/**
 * A public-deck refetch builds new object identities even when nothing the
 * gallery renders has changed. Keep the previous array so mounted cards skip
 * that update.
 */
export function sameGalleryDeckList(prev: readonly Deck[], next: readonly Deck[]): boolean {
  if (prev === next) return true;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i++) {
    const a = prev[i];
    const b = next[i];
    if (a === b) continue;
    if (
      a.id !== b.id ||
      a.updatedAt !== b.updatedAt ||
      a.likeCount !== b.likeCount ||
      a.visibility !== b.visibility ||
      a.status !== b.status ||
      a.title !== b.title
    ) {
      return false;
    }
  }
  return true;
}

export type DeckDateRange = '' | '24h' | '7d' | '30d';
export type DeckLegalityFilter = '' | 'legal' | 'not-legal';
export type DeckContentFilter = '' | 'guide' | 'video' | 'any';

/** Shared public-deck ranking used by the gallery default and Firestore fetches. */
export function compareDecksByGallerySort(a: Deck, b: Deck, sort: DeckGallerySort): number {
  if (sort === 'popular') return b.likeCount - a.likeCount || b.updatedAt - a.updatedAt;
  if (sort === 'newest') return b.createdAt - a.createdAt;
  return b.updatedAt - a.updatedAt;
}

export interface DeckGalleryFilterState {
  searchTerm: string;
  domainFilter: string[];
  /**
   * Champion identities (lowercase, same as `legendIdentityFromRef`).
   * Empty = any legend.
   */
  legendFilter: string[];
  /** Owner handles (no `@`). Empty = any author. */
  authorFilter: string[];
  playstyleFilter: DeckPlaystyle[];
  formatFilter: string;
  legalityFilter: DeckLegalityFilter;
  contentFilter: DeckContentFilter;
  dateRange: DeckDateRange;
  sort: DeckGallerySort;
}

export interface DeckAuthorFilterOption {
  slug: string;
  displayName: string;
  /** Denormalized from the deck; null for decks saved before the field existed. */
  avatarUrl: string | null;
}

export function dateRangeCutoff(range: DeckDateRange): number | null {
  if (!range) return null;
  const day = 24 * 60 * 60 * 1000;
  if (range === '24h') return Date.now() - day;
  if (range === '7d') return Date.now() - 7 * day;
  return Date.now() - 30 * day;
}

/** Unique deck authors for the gallery Author filter menu. */
export function buildDeckAuthorFilterOptions(decks: Deck[]): DeckAuthorFilterOption[] {
  const bySlug = new Map<string, DeckAuthorFilterOption>();
  for (const d of decks) {
    const slug = d.ownerHandle?.trim().toLowerCase();
    if (!slug) continue;
    const existing = bySlug.get(slug);
    const avatarUrl = d.ownerAvatarUrl?.trim() || null;
    if (!existing) {
      bySlug.set(slug, { slug, displayName: d.ownerDisplayName?.trim() || slug, avatarUrl });
      continue;
    }
    // Decks saved before the avatar was denormalized carry none, so keep
    // looking rather than letting the first deck decide the author has none.
    if (!existing.avatarUrl && avatarUrl) existing.avatarUrl = avatarUrl;
  }
  return [...bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
}

/**
 * Shared filter/sort pipeline for the public deck gallery and team decks tab.
 * Returns both the text-filtered list and the pre-text pool used for search suggestions.
 */
export function applyDeckGalleryFilters(
  source: Deck[],
  filters: DeckGalleryFilterState
): { filtered: Deck[]; deckSearchSuggestionPool: Deck[] } {
  let result = source;

  if (filters.formatFilter) {
    result = result.filter((d) => d.formatCode === filters.formatFilter);
  }

  if (filters.domainFilter.length > 0) {
    result = result.filter((d) => {
      const ld = d.legend?.domain ?? [];
      return filters.domainFilter.some((f) => ld.includes(f));
    });
  }

  if (filters.legendFilter.length > 0) {
    const identities = new Set(
      filters.legendFilter.map((id) => id.trim().toLowerCase()).filter(Boolean),
    );
    result = result.filter((d) => identities.has(legendIdentityFromRef(d.legend)));
  }

  if (filters.authorFilter.length > 0) {
    const authors = new Set(filters.authorFilter.map((h) => h.trim().toLowerCase()).filter(Boolean));
    result = result.filter((d) => authors.has(d.ownerHandle.trim().toLowerCase()));
  }

  if (filters.playstyleFilter.length > 0) {
    result = result.filter((d) =>
      filters.playstyleFilter.some((p) => d.playstyle.includes(p))
    );
  }

  if (filters.legalityFilter === 'legal') {
    result = result.filter((d) => d.isValid);
  } else if (filters.legalityFilter === 'not-legal') {
    result = result.filter((d) => !d.isValid);
  }

  if (filters.contentFilter === 'guide') {
    result = result.filter((d) => Boolean(d.guide?.trim()) || Boolean(d.metafy?.url?.trim()));
  } else if (filters.contentFilter === 'video') {
    result = result.filter((d) => Boolean(d.video?.url?.trim()));
  } else if (filters.contentFilter === 'any') {
    result = result.filter(
      (d) =>
        Boolean(d.guide?.trim()) ||
        Boolean(d.video?.url?.trim()) ||
        Boolean(d.metafy?.url?.trim()),
    );
  }

  const cutoff = dateRangeCutoff(filters.dateRange);
  if (cutoff != null) {
    result = result.filter((d) => d.createdAt >= cutoff);
  }

  const sorted = [...result].sort((a, b) => compareDecksByGallerySort(a, b, filters.sort));

  const q = filters.searchTerm.trim().toLowerCase();
  if (!q) {
    return { filtered: sorted, deckSearchSuggestionPool: sorted };
  }

  return {
    filtered: sorted.filter((d) => deckMatchesGalleryTextSearch(d, q)),
    deckSearchSuggestionPool: sorted,
  };
}
